import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './RescheduleSessionModal.css';
import RescheduleSuccessDialog from '../RescheduleSuccessDialog/RescheduleSuccessDialog';
import { axiosInstance } from '../../../utils/tokenUtils';
import dayjs from 'dayjs';
import { toast } from 'react-hot-toast';
import LoadingScreen from '../../LoadingIcon/LoadingScreen';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MorningIcon from '../../../assets/SessionIcon/morning.svg';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '8px',
  width: '600px',
  height: '650px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const formatDate = dateStr => {
  const date = dayjs(dateStr);
  const day = date.format('D');
  const month = date.format('MMM');
  const dayOfWeek = date.format('ddd');
  return `${dayOfWeek} ${day} ${month}`;
};

const RescheduleSessionModal = ({ open, onClose, sessionId }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [animateSlots, setAnimateSlots] = useState(false);
  const [selectedTab, setSelectedTab] = useState('Morning');
  const [specificTimeSlots, setSpecificTimeSlots] = useState([]);
  const [timeFrames, setTimeFrames] = useState({
    Morning: [],
    Afternoon: [],
    Night: [],
  });

  // Animation effect when modal opens
  useEffect(() => {
    if (open) {
      setModalOpen(true);
    } else {
      setTimeout(() => setModalOpen(false), 300);
    }
  }, [open]);

  // Fetch available slots when modal opens
  useEffect(() => {
    if (open && sessionId) {
      fetchAvailableSlots();
    }
    // Reset states when modal closes
    return () => {
      if (!open) {
        setSelectedDate(null);
        setSelectedTimeFrame(null);
        setSelectedSlot(null);
        setError(null);
      }
    };
  }, [open, sessionId]);

  const fetchAvailableSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/teachers/sessions/reschedule/${sessionId}/available_slots`
      );
      const utcSlots = response.data;

      if (utcSlots && utcSlots.length > 0) {
        // Convert UTC slots to local time
        const convertedSlots = utcSlots.map(utcSlot => {
          // Parse the UTC time and convert to local
          const utcTime = new Date(utcSlot);
          const localTime = new Date(utcTime.getTime() - utcTime.getTimezoneOffset() * 60000);

          return {
            utcTime: utcSlot, // Keep the original UTC time for API requests
            localTime: localTime.toISOString(), // Store local time as ISO string
            localDate: dayjs(localTime).format('YYYY-MM-DD'), // Date part for grouping
          };
        });

        // Set the converted slots
        setAllSlots(convertedSlots);

        // Process the slots data
        processSlots(convertedSlots);
      } else {
        setAvailableDates([]);
        setAvailableSlots([]);
        setError('No available slots found');
      }
    } catch (err) {
      console.error('Error fetching available slots:', err);
      setError('Failed to load available slots. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const processSlots = slots => {
    // Group slots by date
    const dateGroups = {};
    slots.forEach(slot => {
      const dateKey = slot.localDate;

      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = [];
      }
      dateGroups[dateKey].push(slot);
    });

    // Format dates for display
    const formattedDates = Object.keys(dateGroups).map(date => {
      return {
        date: formatDate(date),
        value: date,
      };
    });

    setAvailableDates(formattedDates);

    // If we have dates, select the first one by default and populate slots
    if (formattedDates.length > 0) {
      const firstDate = formattedDates[0].value;
      setSelectedDate(firstDate);

      // Immediately populate slots for the first date
      const slotsForDate = slots.filter(slot => slot.localDate === firstDate);
      populateAvailableSlots(slotsForDate);
    }
  };

  const getTimeOfDay = hour => {
    if (hour >= 6 && hour < 12) return 'Morning';
    if (hour >= 12 && hour < 18) return 'Afternoon';
    if (hour >= 18 || hour < 6) return 'Night'; // Night includes 6 PM onwards and early morning hours
  };

  const populateAvailableSlots = slotsForDate => {
    if (slotsForDate.length === 0) {
      setAvailableSlots({
        Morning: [],
        Afternoon: [],
        Night: [],
      });
      setTimeFrames({
        Morning: [],
        Afternoon: [],
        Night: [],
      });
      return;
    }

    // Group slots by time of day
    const groupedSlots = {};
    const hourlyFrames = {
      Morning: new Set(),
      Afternoon: new Set(),
      Night: new Set(),
    };

    slotsForDate.forEach(slot => {
      const localTime = dayjs(slot.localTime);
      const hour = localTime.hour();
      const timeOfDay = getTimeOfDay(hour);

      if (!groupedSlots[timeOfDay]) {
        groupedSlots[timeOfDay] = [];
      }

      // Add the hour to the appropriate time of day
      hourlyFrames[timeOfDay].add(hour);

      const formattedTime = {
        time: formatTime(localTime),
        utcValue: slot.utcTime,
        localValue: slot.localTime,
        hour: hour,
        minute: localTime.minute(),
      };

      groupedSlots[timeOfDay].push(formattedTime);
    });

    // Convert hourly frames sets to arrays of hour ranges
    const timeFramesOutput = {
      Morning: [],
      Afternoon: [],
      Night: [],
    };

    Object.keys(hourlyFrames).forEach(timeOfDay => {
      const hours = Array.from(hourlyFrames[timeOfDay]).sort((a, b) => a - b);
      hours.forEach(hour => {
        timeFramesOutput[timeOfDay].push({
          startHour: hour,
          endHour: hour + 1,
          label: `${formatHour(hour)} - ${formatHour(hour + 1)}`,
          available: true,
        });
      });
    });

    // Sort existing frames only (do not add unavailable ones)
    Object.keys(timeFramesOutput).forEach(timeOfDay => {
      if (timeOfDay === 'Night') {
        timeFramesOutput[timeOfDay].sort((a, b) => {
          const hourA = a.startHour < 6 ? a.startHour + 24 : a.startHour;
          const hourB = b.startHour < 6 ? b.startHour + 24 : b.startHour;
          return hourA - hourB;
        });
      } else {
        timeFramesOutput[timeOfDay].sort((a, b) => a.startHour - b.startHour);
      }
    });

    setTimeFrames(timeFramesOutput);
    setAvailableSlots(groupedSlots);

    // Select the first tab that has available slots
    const firstAvailableTab =
      Object.keys(groupedSlots).find(key => groupedSlots[key].length > 0) || 'Morning';
    setSelectedTab(firstAvailableTab);

    // If no slots available for any tab, show a message
    const hasAnySlots = Object.values(groupedSlots).some(slots => slots.length > 0);
    if (!hasAnySlots) {
      setError('No available slots found for this date. Please try another date.');
    }
  };

  const addMissingHours = timeFramesOutput => {
    // Define the complete range of hours for each time of day
    const completeRanges = {
      Morning: Array.from({ length: 6 }, (_, i) => i + 6), // 6AM to 11AM
      Afternoon: Array.from({ length: 6 }, (_, i) => i + 12), // 12PM to 5PM
      Night: Array.from({ length: 12 }, (_, i) => (i + 18) % 24), // 6PM to 5AM (next day)
    };

    Object.keys(completeRanges).forEach(timeOfDay => {
      const existingHours = timeFramesOutput[timeOfDay].map(frame => frame.startHour);
      const missingHours = completeRanges[timeOfDay].filter(hour => !existingHours.includes(hour));

      missingHours.forEach(hour => {
        timeFramesOutput[timeOfDay].push({
          startHour: hour,
          endHour: hour + 1,
          label: `${formatHour(hour)} - ${formatHour(hour + 1)}`,
          available: false,
        });
      });

      // Sort the time frames by hour, handling the Night wrap-around
      if (timeOfDay === 'Night') {
        timeFramesOutput[timeOfDay].sort((a, b) => {
          // Handle wrap-around for Night (18-23, then 0-4)
          const hourA = a.startHour < 6 ? a.startHour + 24 : a.startHour;
          const hourB = b.startHour < 6 ? b.startHour + 24 : b.startHour;
          return hourA - hourB;
        });
      } else {
        timeFramesOutput[timeOfDay].sort((a, b) => a.startHour - b.startHour);
      }
    });
  };

  const formatHour = hour => {
    const h = hour % 12 || 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${h} ${ampm}`;
  };

  const formatTime = time => {
    const hour = time.hour() % 12 || 12;
    const minute = time.minute();
    const period = time.hour() >= 12 ? 'PM' : 'AM';
    return `${hour}${minute > 0 ? ':' + minute.toString().padStart(2, '0') : ''} ${period}`;
  };

  const handleDateSelect = date => {
    if (date === selectedDate) return; // Don't process if clicking the same date

    // Animate slots transition out
    setAnimateSlots(true);

    // Small delay before changing data to allow animation to play
    setTimeout(() => {
      setSelectedDate(date);
      setSelectedTimeFrame(null);
      setSelectedSlot(null);

      // Filter slots for the selected date
      const slotsForDate = allSlots.filter(slot => slot.localDate === date);
      populateAvailableSlots(slotsForDate);

      // Reset animation state after changing data
      setTimeout(() => {
        setAnimateSlots(false);
      }, 50);
    }, 300);
  };

  const handleTabSelect = tab => {
    setSelectedTab(tab);
    setSelectedTimeFrame(null);
    setSelectedSlot(null);
    setSpecificTimeSlots([]);
  };

  const handleTimeFrameSelect = timeFrame => {
    if (!timeFrame.available) return;

    setSelectedTimeFrame(timeFrame);

    // Filter slots for the selected time frame
    const slotsInTimeFrame = availableSlots[selectedTab].filter(
      slot => slot.hour >= timeFrame.startHour && slot.hour < timeFrame.endHour
    );

    // Group by specific time
    const specificSlots = [];
    slotsInTimeFrame.forEach(slot => {
      const time = dayjs(slot.localValue);
      const formattedTime = {
        startTime: `${formatTime(time)}`,
        endTime: `${formatTime(time.add(60, 'minute'))}`, // Sessions are 60 minutes long
        utcValue: slot.utcValue,
        localValue: slot.localValue,
      };
      specificSlots.push(formattedTime);
    });

    setSpecificTimeSlots(specificSlots);
  };

  const handleSlotSelect = slot => {
    setSelectedSlot(slot.utcValue);
  };

  const handleRequest = async () => {
    if (selectedSlot) {
      try {
        setLoading(true);
        setError(null);

        // Make API call to request reschedule with the UTC time
        const response = await axiosInstance.post(
          `/teachers/sessions/reschedule/${sessionId}/request`,
          {
            new_start_time: selectedSlot,
          }
        );

        console.log('Reschedule response:', response);

        // Only close the modal and show success dialog if successful
        onClose();
        setShowSuccess(true);
      } catch (error) {
        console.error('Error rescheduling session:', error);

        // Handle different types of errors
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          const status = error.response.status;
          const errorDetail = error.response.data?.detail || 'An error occurred while rescheduling';

          if (status === 500) {
            toast.error('Internal Server Error. Please try again later.');
          } else if (status === 400) {
            toast.error(errorDetail);
          } else if (status === 401 || status === 403) {
            toast.error('You are not authorized to perform this action');
          } else if (status === 404) {
            toast.error('Session not found');
          } else {
            toast.error(`Error: ${errorDetail}`);
          }

          // Update error state for display in the UI
          setError(errorDetail);
        } else if (error.request) {
          // The request was made but no response was received
          toast.error('No response from server. Please check your internet connection.');
          setError('Network error. Please try again later.');
        } else {
          // Something happened in setting up the request that triggered an error
          toast.error('Failed to submit request. Please try again.');
          setError('Request failed. Please try again later.');
        }

        // Keep modal open so user can see the error
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setSelectedDate(null);
    setSelectedTimeFrame(null);
    setSelectedSlot(null);
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={style} className={`modal-container ${modalOpen ? 'modal-open' : ''}`}>
          {/* Header - Fixed */}
          <Box className="modal-header flex justify-between items-center p-4 border-b border-[#D5DEE3]">
            <Typography className="reschedule-session-title">Reschedule Session</Typography>
            <CloseIcon onClick={onClose} sx={{ cursor: 'pointer', color: '#666666' }} />
          </Box>

          {/* Content - Scrollable with auto height to push footer to bottom */}
          <Box className="modal-content p-4 flex-grow overflow-hidden">
            {loading ? (
              <Box className="flex justify-center items-center h-full">
                <LoadingScreen />
              </Box>
            ) : (
              <>
                {/* Date Selection - Fixed, not scrollable */}
                <Box className="mb-4">
                  <Typography className="reschedule-section-title">
                    Select Reschedule Date
                  </Typography>
                  <Box className="flex gap-2 mt-2 flex-wrap date-chip-container">
                    {availableDates.length > 0 ? (
                      availableDates.map(date => (
                        <Box
                          key={date.value}
                          onClick={() => handleDateSelect(date.value)}
                          className={`reschedule-date-chip ${
                            selectedDate === date.value ? 'selected' : ''
                          }`}
                        >
                          {date.date}
                        </Box>
                      ))
                    ) : (
                      <Typography className="text-gray-500">No dates available</Typography>
                    )}
                  </Box>
                </Box>

                {/* Time of Day Tabs */}
                <Box className="time-of-day-tabs mb-4">
                  <Typography className="reschedule-section-title mb-3">
                    Select Time of Day
                  </Typography>
                  <Box className="tab-container">
                    {availableSlots.Morning && availableSlots.Morning.length > 0 && (
                      <Box
                        className={`tab ${selectedTab === 'Morning' ? 'active' : ''}`}
                        onClick={() => handleTabSelect('Morning')}
                      >
                        <img src={MorningIcon} alt="Morning" className="tab-icon" />
                        <span>Morning</span>
                        {availableSlots.Morning && availableSlots.Morning.length > 0 && (
                          <span className="slot-count">{availableSlots.Morning.length}</span>
                        )}
                      </Box>
                    )}
                    {availableSlots.Afternoon && availableSlots.Afternoon.length > 0 && (
                      <Box
                        className={`tab ${selectedTab === 'Afternoon' ? 'active' : ''}`}
                        onClick={() => handleTabSelect('Afternoon')}
                      >
                        <LightModeOutlinedIcon className="tab-icon" />
                        <span>Afternoon</span>
                        {availableSlots.Afternoon && availableSlots.Afternoon.length > 0 && (
                          <span className="slot-count">{availableSlots.Afternoon.length}</span>
                        )}
                      </Box>
                    )}
                    {availableSlots.Night && availableSlots.Night.length > 0 && (
                      <Box
                        className={`tab ${selectedTab === 'Night' ? 'active' : ''}`}
                        onClick={() => handleTabSelect('Night')}
                      >
                        <NightsStayOutlinedIcon className="tab-icon" />
                        <span>Night</span>
                        {availableSlots.Night && availableSlots.Night.length > 0 && (
                          <span className="slot-count">{availableSlots.Night.length}</span>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Time Frame Selection */}
                {timeFrames[selectedTab] && timeFrames[selectedTab].length > 0 && (
                  <Box className="time-frame-section mb-4">
                    <Typography className="reschedule-section-title mb-3">
                      Select Time Range
                    </Typography>
                    <Box className="time-frames-container">
                      {timeFrames[selectedTab].map((timeFrame, index) => (
                        <Box
                          key={index}
                          className={`time-frame-chip ${
                            selectedTimeFrame?.startHour === timeFrame.startHour ? 'selected' : ''
                          } ${!timeFrame.available ? 'disabled' : ''}`}
                          onClick={() => timeFrame.available && handleTimeFrameSelect(timeFrame)}
                        >
                          {selectedTab === 'Morning' ? (
                            <img src={MorningIcon} alt="Morning" className="time-icon" />
                          ) : selectedTab === 'Afternoon' ? (
                            <LightModeOutlinedIcon className="time-icon" />
                          ) : (
                            <NightsStayOutlinedIcon className="time-icon" />
                          )}
                          <span className="time-label">{timeFrame.label}</span>
                          {!timeFrame.available && (
                            <span className="unavailable-indicator">No slots</span>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Pick specific time slots */}
                {selectedTimeFrame && specificTimeSlots.length > 0 && (
                  <Box className="specific-time-section">
                    <Typography className="reschedule-section-title mb-3">
                      Select Specific Time
                    </Typography>
                    <Box className="specific-times-container">
                      {specificTimeSlots.map((slot, index) => (
                        <Box
                          key={index}
                          className={`specific-time-chip ${
                            selectedSlot === slot.utcValue ? 'selected' : ''
                          }`}
                          onClick={() => handleSlotSelect(slot)}
                        >
                          {selectedSlot === slot.utcValue ? (
                            <>
                              <span className="time-start">{slot.startTime}</span>
                              <span className="time-separator">-</span>
                              <span className="time-end">{slot.endTime}</span>
                            </>
                          ) : (
                            <span className="time-single">{slot.startTime}</span>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* No slots available message */}
                {selectedTab &&
                  (!availableSlots[selectedTab] || availableSlots[selectedTab].length === 0) && (
                    <Box className="no-slots-message">
                      <Typography className="text-gray-500 text-center">
                        No available slots for {selectedTab.toLowerCase()} on this date.
                      </Typography>
                    </Box>
                  )}

                {/* Error message */}
                {error && (
                  <Box className="error-message">
                    <Typography className="error-text">{error}</Typography>
                  </Box>
                )}
                {/* Selected Time Summary */}
                {selectedSlot && (
                  <Box className="selected-time-summary">
                    <Typography className="reschedule-section-title mb-2">Selected Time</Typography>
                    <Box className="selected-time-display">
                      <span className="selected-date">
                        {selectedDate && formatDate(selectedDate)}
                      </span>
                      <span className="selected-time">
                        {selectedTimeFrame && `${selectedTimeFrame.label}`}
                      </span>
                      <span className="selected-specific-time">
                        {specificTimeSlots.find(slot => slot.utcValue === selectedSlot)?.startTime}{' '}
                        - {specificTimeSlots.find(slot => slot.utcValue === selectedSlot)?.endTime}
                      </span>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box className="modal-footer flex justify-end gap-3 p-4 border-t border-[#D5DEE3]">
            <button className="reschedule-cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="reschedule-request-button"
              onClick={handleRequest}
              disabled={!selectedSlot || loading}
            >
              {loading ? 'Requesting...' : 'Request'}
            </button>
          </Box>
        </Box>
      </Modal>

      <RescheduleSuccessDialog open={showSuccess} onClose={handleSuccessClose} />
    </>
  );
};

export default RescheduleSessionModal;
